import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { GhostButton, ActionButton, FormField, inputClass } from "./Primitives";
import { Modal } from "./Modal";
import { Sparkles, Save, AlertTriangle, ChevronDown, Trash2 } from "lucide-react";

/**
 * PortalTemplatesBar — inline strip on `/clients/:id/portal-config` that lets
 * admins:
 *   • Apply a template (system preset or custom) to THIS client
 *   • Save the current config as a new custom template
 *
 * "Apply to all clients" lives on the standalone /settings/portal-templates
 * page since it&apos;s a heavier action.
 *
 * Props:
 *   clientId      target client (for apply-to-this)
 *   getSnapshot   fn returning current sections + metrics for save-as
 *   onApplied     called after successful apply so the parent can refetch
 */
export default function PortalTemplatesBar({ clientId, getSnapshot, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [confirmApply, setConfirmApply] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portal/config-templates");
      setTemplates(data?.templates || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load templates"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const applyTemplate = async () => {
    if (!selectedId) return;
    setApplying(true);
    try {
      await api.post(`/portal/config-templates/${selectedId}/apply`, {
        client_id: clientId,
      });
      toast.success("Template applied — layout updated");
      setConfirmApply(null);
      if (typeof onApplied === "function") onApplied();
    } catch (err) {
      toast.error(errorMessage(err, "Apply failed"));
    } finally {
      setApplying(false);
    }
  };

  const chosen = templates.find((t) => t.id === selectedId);

  return (
    <div
      className="border border-[#27272A] bg-[#121212] p-4 flex items-center gap-3 flex-wrap"
      data-testid="portal-templates-bar"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="size-4 text-[#FF3B30]" />
        <div className="label-mute whitespace-nowrap">Apply template</div>
      </div>
      <div className="relative flex-1 min-w-[220px]">
        <select
          data-testid="portal-templates-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          disabled={loading}
          className={`${inputClass} appearance-none pr-8`}
        >
          <option value="">— pick a preset —</option>
          {templates.filter((t) => t.is_system).length > 0 && (
            <optgroup label="System presets">
              {templates.filter((t) => t.is_system).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </optgroup>
          )}
          {templates.filter((t) => !t.is_system).length > 0 && (
            <optgroup label="Your saved templates">
              {templates.filter((t) => !t.is_system).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        <ChevronDown className="size-4 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
      <GhostButton
        data-testid="portal-templates-apply-btn"
        onClick={() => setConfirmApply(chosen)}
        disabled={!chosen || applying}
        className={!chosen ? "opacity-50 cursor-not-allowed" : ""}
      >
        Apply to this client
      </GhostButton>
      <GhostButton
        data-testid="portal-templates-save-as-btn"
        onClick={() => setSaveOpen(true)}
      >
        <Save className="size-4" /> Save as template
      </GhostButton>
      {chosen?.description && (
        <div className="basis-full text-xs text-zinc-500">
          <span className="uppercase tracking-widest text-[10px] text-zinc-600 mr-1">Preview:</span>
          {chosen.description}
        </div>
      )}

      {confirmApply && (
        <Modal
          onClose={() => setConfirmApply(null)}
          title="Apply template?"
          testId="portal-templates-confirm-modal"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3 border border-amber-500/40 bg-amber-500/5 p-3">
              <AlertTriangle className="size-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-300">
                <div className="font-bold text-zinc-100">{confirmApply.name}</div>
                <div className="mt-1">
                  Layout dimensions (metric visibility, order, section labels) will be replaced.
                  Per-metric labels, tooltips, thresholds, health-score weights, and approval overrides
                  on this client are <span className="text-zinc-100 font-bold">preserved</span>.
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
              <GhostButton onClick={() => setConfirmApply(null)}>Cancel</GhostButton>
              <ActionButton
                data-testid="portal-templates-confirm-apply"
                onClick={applyTemplate}
                disabled={applying}
              >
                {applying ? "Applying…" : "Apply template"}
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}

      {saveOpen && (
        <SaveAsTemplateModal
          onClose={() => setSaveOpen(false)}
          onSaved={(created) => {
            setTemplates((prev) => [...prev, created]);
            setSaveOpen(false);
            toast.success(`Template "${created.name}" saved`);
          }}
          getSnapshot={getSnapshot}
        />
      )}
    </div>
  );
}


function SaveAsTemplateModal({ onClose, onSaved, getSnapshot }) {
  const [name, setName]             = useState("");
  const [vertical, setVertical]     = useState("custom");
  const [description, setDescription] = useState("");
  const [saving, setSaving]         = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const snapshot = getSnapshot();
      const { data } = await api.post("/portal/config-templates", {
        name: name.trim(),
        vertical: vertical.trim() || "custom",
        description: description.trim(),
        snapshot,
      });
      onSaved(data);
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Save as template" testId="portal-templates-save-modal">
      <div className="space-y-4">
        <FormField label="Template name *">
          <input
            data-testid="portal-templates-save-name"
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Bay Area retail default"
            autoFocus
          />
        </FormField>
        <FormField label="Vertical tag">
          <input
            data-testid="portal-templates-save-vertical"
            className={inputClass}
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
            placeholder="custom / retail / construction / ..."
          />
        </FormField>
        <FormField label="Description">
          <textarea
            data-testid="portal-templates-save-description"
            className={`${inputClass} min-h-[80px]`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this preset optimises for — helpful when picking later."
          />
        </FormField>
        <div className="text-xs text-zinc-500">
          Captures the current section list + metric layout (enabled / order / section).
          Per-metric labels, thresholds, and per-site overrides are <span className="text-zinc-300">not</span> included so
          applying this template later is non-destructive.
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#27272A]">
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <ActionButton
            data-testid="portal-templates-save-submit"
            onClick={submit}
            disabled={saving || !name.trim()}
          >
            <Save className="size-4" /> {saving ? "Saving…" : "Save template"}
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}


// Delete icon re-export so parent pages can render the same in table rows.
export { Trash2 as DeleteIcon };
